import { useMemo, useState, useRef, useCallback } from 'react';
import { useClickAway } from 'ahooks';
import type { Msg } from '@halo/chat-sdk-react';
import { type User } from '@halo/chat-sdk-react';
import { Menu } from '@chakra-ui/react';
import { Emoji } from '@/components/Emoji';
import { useStaticAppStore } from '@/store/client';

export function EmojiMenu({
  data,
  actions,
}: {
  data: { message: Msg; user: User };
  actions?: { switchVisible: (visible: boolean) => void; switchOpen: (open: boolean) => void };
}) {
  const [emojiPanelOpen, setEmojiPanelOpen] = useState<boolean>(false);
  const emojiPanelRef = useRef<HTMLDivElement>(null);
  const { client, activatedSessionId, user } = useStaticAppStore();

  const user_id = useMemo(() => user?.user_id, [user?.user_id]);

  const handleEmojiSelect = useCallback(
    async (emoji_id: string, op_type: 1 | 2) => {
      if (!client || !activatedSessionId) return;
      // if had, cancel
      await client.Api.messages.editEmojiReaction(
        activatedSessionId,
        data.message.message_id,
        emoji_id,
        op_type,
        user_id
          ? {
              client_msg_id: data.message.cli_msg_id,
              user_id: user_id,
            }
          : undefined
      );
    },
    [client, activatedSessionId, user_id, data.message.message_id, data.message.cli_msg_id]
  );

  useClickAway((e) => {
    if (!emojiPanelOpen) return;
    e.stopPropagation();
    setEmojiPanelOpen(false);
    actions?.switchVisible(true);
    actions?.switchOpen(true);
  }, emojiPanelRef);

  if (!emojiPanelOpen)
    return (
      <Menu.Content
        backdropFilter="blur(6px)"
        bg="whiteAlpha.70"
        borderRadius="full"
        p="0"
        mb="2"
      >
        <Emoji.Quick
          onSelect={(emoji_id) => {
            const op_type = data.message.emoji_reaction
              ?.find((item) => item.emoji_id === emoji_id)
              ?.reaction_log.find((item) => item.user_id === user_id)
              ? 2
              : 1;
            handleEmojiSelect(emoji_id, op_type);
            actions?.switchOpen(false);
          }}
          onMore={(e) => {
            e.preventDefault();
            e.stopPropagation();
            actions?.switchVisible(false);
            setEmojiPanelOpen(true);
          }}
        />
      </Menu.Content>
    );

  return (
    <Menu.Content ref={emojiPanelRef} p="0">
      <Emoji.Panel
        onSelect={(emoji_id) => {
          setEmojiPanelOpen(false);
          actions?.switchOpen(false);
          const op_type = data.message.emoji_reaction
            ?.find((item) => item.emoji_id === emoji_id)
            ?.reaction_log.find((item) => item.user_id === user_id)
            ? 2
            : 1;
          handleEmojiSelect(emoji_id, op_type);
        }}
      />
    </Menu.Content>
  );
}
